import {
  type BrowserSessionSnapshot,
  type DevTunnelRecord,
  type EnvironmentApi,
  type EnvironmentId,
  type ScopedThreadRef,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { type MutableRefObject, useCallback } from "react";
import { useBrowserStateStore } from "~/browserStateStore";
import { ensureEnvironmentApi } from "~/environmentApi";
import {
  browserSessionHasVisibleAddressIntent,
  localPreviewSessionAttemptCount,
  shouldRetryLocalPreviewSessionStartup,
  withBrowserOperationTimeout,
} from "./BrowserPreviewPanel.logic";
import { type BrowserOptionsAction } from "./BrowserPreviewPanel.sessionControlActions";

const BROWSER_SESSION_START_TIMEOUT_MS = 180_000;
const BROWSER_SESSION_CLOSE_TIMEOUT_MS = 12_000;
const LOCAL_PREVIEW_BROWSER_SESSION_RETRY_DELAY_MS = 1_000;

type BrowserApi = NonNullable<EnvironmentApi["browser"]>;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function closeBrowserSessionBeforeRestart(options: {
  readonly browserApi: BrowserApi;
  readonly environmentId: EnvironmentId;
  readonly session: BrowserSessionSnapshot | null;
  readonly threadId: ThreadId;
}): Promise<void> {
  try {
    await withBrowserOperationTimeout(
      "Browser session close",
      options.browserApi.closeSession({
        threadId: options.threadId,
        environmentId: options.environmentId,
        ...(options.session?.browserId ? { browserId: options.session.browserId } : {}),
      }),
      BROWSER_SESSION_CLOSE_TIMEOUT_MS,
    );
  } catch {
    // Restart recovery is best-effort cleanup before reopening the requested page.
  }
}

async function openBrowserSessionAtUrl(options: {
  readonly browserApi: BrowserApi;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
  readonly environmentId: EnvironmentId;
  readonly initialUrl: string;
  readonly threadId: ThreadId;
}): Promise<BrowserSessionSnapshot> {
  const opened = await withBrowserOperationTimeout(
    "Browser session restart",
    options.browserApi.openSession({
      threadId: options.threadId,
      environmentId: options.environmentId,
      initialUrl: options.initialUrl,
    }),
    BROWSER_SESSION_START_TIMEOUT_MS,
  );
  if (browserSessionHasVisibleAddressIntent(opened, options.initialUrl, options.devTunnelRecords)) {
    return opened;
  }

  const result = await withBrowserOperationTimeout(
    "Browser tab open",
    options.browserApi.command({
      threadId: options.threadId,
      environmentId: options.environmentId,
      browserId: opened.browserId,
      command: { type: "newTab", url: options.initialUrl, makeActive: true },
    }),
    BROWSER_SESSION_START_TIMEOUT_MS,
  );
  return result.snapshot;
}

async function restartBrowserSessionOnce(options: {
  readonly browserApi: BrowserApi;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
  readonly environmentId: EnvironmentId;
  readonly initialUrl: string;
  readonly session: BrowserSessionSnapshot | null;
  readonly setSessionSnapshot: (
    threadRef: ScopedThreadRef,
    snapshot: BrowserSessionSnapshot,
  ) => void;
  readonly threadId: ThreadId;
  readonly threadRef: ScopedThreadRef;
}): Promise<boolean> {
  await closeBrowserSessionBeforeRestart(options);
  useBrowserStateStore.getState().clearBrowserState(options.threadRef);
  const snapshot = await openBrowserSessionAtUrl(options);
  options.setSessionSnapshot(options.threadRef, snapshot);
  return browserSessionHasVisibleAddressIntent(
    snapshot,
    options.initialUrl,
    options.devTunnelRecords,
  );
}

async function restartBrowserSessionWithRetry(options: {
  readonly browserApi: BrowserApi;
  readonly devTunnelRecordsRef: MutableRefObject<ReadonlyArray<DevTunnelRecord>>;
  readonly environmentId: EnvironmentId;
  readonly handleBrowserError: (error: unknown) => void;
  readonly initialUrl: string;
  readonly session: BrowserSessionSnapshot | null;
  readonly setSessionSnapshot: (
    threadRef: ScopedThreadRef,
    snapshot: BrowserSessionSnapshot,
  ) => void;
  readonly threadId: ThreadId;
  readonly threadRef: ScopedThreadRef;
}): Promise<boolean> {
  const attempts = localPreviewSessionAttemptCount(
    options.initialUrl,
    options.devTunnelRecordsRef.current,
  );
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await restartBrowserSessionOnce({
        ...options,
        devTunnelRecords: options.devTunnelRecordsRef.current,
      });
    } catch (error) {
      const canRetry =
        attempt < attempts &&
        shouldRetryLocalPreviewSessionStartup({
          url: options.initialUrl,
          error,
          devTunnelRecords: options.devTunnelRecordsRef.current,
        });
      if (!canRetry) {
        options.handleBrowserError(error);
        return false;
      }
      await sleep(LOCAL_PREVIEW_BROWSER_SESSION_RETRY_DELAY_MS);
    }
  }
  return false;
}

interface BrowserSessionRestartOptions {
  readonly devTunnelRecordsRef: MutableRefObject<ReadonlyArray<DevTunnelRecord>>;
  readonly environmentId: EnvironmentId;
  readonly handleBrowserError: (error: unknown) => void;
  readonly invalidateInFlightBrowserCommands: () => void;
  readonly session: BrowserSessionSnapshot | null;
  readonly setBrowserRuntimeError: (value: string | null) => void;
  readonly setLifecycleAction: (value: BrowserOptionsAction | null) => void;
  readonly setSessionSnapshot: (
    threadRef: ScopedThreadRef,
    snapshot: BrowserSessionSnapshot,
  ) => void;
  readonly threadId: ThreadId;
  readonly threadRef: ScopedThreadRef;
}

interface BrowserSessionRestartControls {
  readonly restartBrowserSessionAtUrl: (initialUrl: string) => Promise<boolean>;
}

export function useBrowserSessionRestart({
  devTunnelRecordsRef,
  environmentId,
  handleBrowserError,
  invalidateInFlightBrowserCommands,
  session,
  setBrowserRuntimeError,
  setLifecycleAction,
  setSessionSnapshot,
  threadId,
  threadRef,
}: BrowserSessionRestartOptions): BrowserSessionRestartControls {
  const restartBrowserSessionAtUrl = useCallback(
    async (initialUrl: string): Promise<boolean> => {
      const api = ensureEnvironmentApi(environmentId);
      const browserApi = api.browser;
      if (!browserApi) {
        handleBrowserError("Browser runtime is not available for this environment.");
        return false;
      }

      invalidateInFlightBrowserCommands();
      setLifecycleAction("restart");
      setBrowserRuntimeError(null);
      try {
        return await restartBrowserSessionWithRetry({
          browserApi,
          devTunnelRecordsRef,
          environmentId,
          handleBrowserError,
          initialUrl,
          session,
          setSessionSnapshot,
          threadId,
          threadRef,
        });
      } catch (error) {
        handleBrowserError(error);
        return false;
      } finally {
        setLifecycleAction(null);
      }
    },
    [
      devTunnelRecordsRef,
      environmentId,
      handleBrowserError,
      invalidateInFlightBrowserCommands,
      session,
      setBrowserRuntimeError,
      setLifecycleAction,
      setSessionSnapshot,
      threadId,
      threadRef,
    ],
  );

  return { restartBrowserSessionAtUrl };
}
